import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetUserDetailsQuery, useEditUserMutation } from '../services/auth/authApi';
import { toast } from 'react-toastify';
import Loading from '../components/Loading';

import SuperAdminForm from '../components/forms/SuperAdminForm';
import MiniAdminForm from '../components/forms/MiniAdminForm';
import MasterForm from '../components/forms/MasterForm';
import SuperForm from '../components/forms/SuperForm';
import AgentForm from '../components/forms/AgentForm';
import ClientForm from '../components/forms/ClientForm';

const USER_TYPE_NAMES = {
  1: 'Admin',
  2: 'SuperAdmin',
  3: 'MiniAdmin',
  4: 'Master',
  5: 'Super',
  6: 'Agent',
  7: 'Client',
};

const EditUserPage = () => {
  const navigate = useNavigate();
  const { userId } = useParams();

  const { data: userDetails, isLoading, error } = useGetUserDetailsQuery(userId);
  const [editUser, { isLoading: isUpdating }] = useEditUserMutation();

  const [userTypeName, setUserTypeName] = useState('');
  const [hierarchyLevel, setHierarchyLevel] = useState(null);
  const [parentDetails, setParentDetails] = useState(null);

  const [formData, setFormData] = useState({
    fullName: '',
    contactNo: '',
    status: 'active',
    myShare: '',
    companyShare: '',
    matchCommission: '',
    sessionCommission: '',
    casinoShare: '',
    companyCasinoShare: '',
    casinoCommission: '',
    matkaCommission: '',
    exposureLimit: '',
    shareType: 'fixed',
    commType: 'Bet by bet',
  });

  useEffect(() => {
    if (userDetails?.data) {
      const user = userDetails.data;
      setHierarchyLevel(user.hierarchyLevel);
      setUserTypeName(USER_TYPE_NAMES[user.hierarchyLevel] || 'User');
      setParentDetails(user.parentId || null);
      setFormData(prev => ({
        ...prev,
        fullName: user.fullName || user.username || '',
        contactNo: user.contactNo || '',
        status: user.isActive ? 'active' : 'inactive',
        myShare: user.shares?.myShare ?? '',
        matchCommission: user.shares?.matchCommission ?? '',
        sessionCommission: user.shares?.sessionCommission ?? '',
        casinoShare: user.shares?.casinoShare ?? '',
        casinoCommission: user.shares?.casinoCommission ?? '',
        matkaCommission: user.shares?.matkaCommission ?? '',
        exposureLimit: user.exposureLimit ?? user.availableBalance ?? '',
        commType: user.commissionType || 'Bet by bet',
        shareType: 'fixed',
      }));
    }
  }, [userDetails]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleNumberWheel = (e) => {
    e.target.blur();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        myShare: parseFloat(formData.myShare) || 0,
        matchCommission: parseFloat(formData.matchCommission) || 0,
        sessionCommission: parseFloat(formData.sessionCommission) || 0,
        casinoShare: parseFloat(formData.casinoShare) || 0,
        casinoCommission: parseFloat(formData.casinoCommission) || 0,
        matkaCommission: parseFloat(formData.matkaCommission) || 0,
      };
      await editUser({ userId, data: payload }).unwrap();
      toast.success('User updated successfully!');
      navigate(-1);
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to update user');
    }
  };

  const renderFormComponent = () => {
    const commonProps = {
      formData,
      handleChange,
      handleNumberWheel,
      handleSubmit,
      loading: isUpdating,
      createUserLoading: isUpdating,
      error: null,
      userProfileData: null,
      parentDetails,
      isEdit: true,
    };

    switch (hierarchyLevel) {
      case 2: return <SuperAdminForm {...commonProps} />;
      case 3: return <MiniAdminForm {...commonProps} />;
      case 4: return <MasterForm {...commonProps} />;
      case 5: return <SuperForm {...commonProps} />;
      case 6: return <AgentForm {...commonProps} />;
      case 7: return <ClientForm {...commonProps} />;
      default: return <MiniAdminForm {...commonProps} />;
    }
  };

  if (isLoading) {
    return (
      <div className="w-full flex justify-center items-center min-h-[400px]">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full flex justify-center items-center min-h-[400px]">
        <div className="text-lg text-red-500">Error loading user details</div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="max-w-5xl mx-auto px-2 py-4">
        <div className="bg-white rounded shadow border border-gray-200">
          <div className="bg-gray-600 px-5 py-3 flex items-center justify-between">
            <span className="text-2xl text-white font-normal capitalize">
              Update {userTypeName.toLowerCase()}
            </span>
            <button
              onClick={() => navigate(-1)}
              className="bg-blue-500 text-white px-4 py-2 hover:bg-blue-600"
              type="button"
            >
              Back
            </button>
          </div>
          {hierarchyLevel && renderFormComponent()}
        </div>
      </div>
    </div>
  );
};

export default EditUserPage;
